/**
 * Query key factory for notifications
 * 
 * These keys are used to identify and organize React Query cache entries
 * related to notifications. Following TanStack Query best practices for structured keys.
 * 
 * Query keys are organized hierarchically to allow for selective invalidation:
 * - notificationKeys.all: Invalidates all notification-related queries
 * - notificationKeys.lists(): Invalidates the main notification list
 * - notificationKeys.detail(id): Invalidates a specific notification
 * 
 * Usage examples:
 * - notificationKeys.all - Base key for all notification data
 * - notificationKeys.lists() - Key for notification lists
 * - notificationKeys.detail('123') - Key for a specific notification
 * 
 * @example
 * // Invalidate all notification data
 * queryClient.invalidateQueries({ queryKey: notificationKeys.all });
 * 
 * @example
 * // Fetch notifications list
 * useQuery({ queryKey: notificationKeys.lists(), queryFn: fetchNotifications });
 */
export const notificationKeys = {
  /**
   * Base key for all notification-related queries
   * Invalidating this key will invalidate all notification queries
   */
  all: ['notifications'] as const,
  
  /**
   * Key for notification lists
   * Used for fetching all notifications
   * 
   * @returns Tuple of ['notifications', 'list']
   */
  lists: () => [...notificationKeys.all, 'list'] as const,
  
  /**
   * Key for notification details
   * Used for fetching or invalidating a specific notification
   * 
   * @param id - The notification ID
   * @returns Tuple of ['notifications', 'detail', id]
   */
  detail: (id: string) => [...notificationKeys.all, 'detail', id] as const,
  
  /**
   * Key for unread notification count
   * Used for fetching or invalidating the unread notification count
   * 
   * @returns Tuple of ['notifications', 'unread-count']
   */
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
};